import { useEditor, useNode } from '@craftjs/core';
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { getCloneTree } from 'src/components/formEditor/uiEditor/uiEditor-helper';
import { useAnswerRowConditional } from 'src/features/generalForms/answerPreview/hooks/useAnswerRowConditional';
import { generateNestedDataKey } from 'src/features/generalForms/builder/layout/panels/helpers/helper';
import {
  FormRepeatableContainerComponent,
  FormRepeatableSection,
} from 'src/features/generalForms/builder/types/RepeatableContainer';
import { RPDF } from 'src/features/report/react-pdf';
import { FieldGroup } from 'src/features/report/records/components/FieldGroup';

export const RepeatableContainer: FormRepeatableContainerComponent = ({ dataKey: rootDataKey, children, title }) => {
  const { actions, query } = useEditor();
  const { visible } = useAnswerRowConditional();

  const { node: parentNode } = useNode((n) => ({
    styles: n.data.custom,
    node: n,
  }));

  const form = useFormContext();
  const fieldOrder = useWatch({ control: form.control, name: `${rootDataKey!}.order` }) ?? [];
  const addSection = useCallback(
    (sectionDataKey: string) => {
      const newTree = getCloneTree(query, parentNode.data.linkedNodes.id, generateNestedDataKey(query, sectionDataKey));
      actions.addNodeTree(newTree, parentNode.id);
    },
    [actions, parentNode.data.linkedNodes.id, parentNode.id, query],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      fieldOrder.forEach(addSection);
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldOrder]);

  if (!visible) return <RPDF.View />;

  return title ? <FieldGroup title={title}>{children}</FieldGroup> : <RPDF.View>{children}</RPDF.View>;
};

export const RepeatableSection: FormRepeatableSection = ({ children, sectionTitle }) => {
  const { query } = useEditor();
  const { id, parentNodeId } = useNode((n) => ({
    parentNodeId: n.data.parent,
    styles: n.data.custom,
  }));

  const sectionIndex = useMemo(
    () => query.node(parentNodeId).descendants(false, 'childNodes').indexOf(id) + 1,
    [id, parentNodeId, query],
  );

  return (
    <RPDF.View style={{ marginVertical: 8 }}>
      {!!sectionTitle && (
        <RPDF.Text
          minPresenceAhead={40}
          style={[RPDF.typography.heading5b, RPDF.tableStyles.divider, { paddingVertical: 4 }]}
        >
          {`${sectionTitle} ${sectionIndex}`}
        </RPDF.Text>
      )}
      {children}
    </RPDF.View>
  );
};
